import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import ChevronSvg from "../../assets/svgTsx/chevron";
import { DEFAULT_PAGE_SIZE } from "../../constants/miscConstants";

interface StyledTableProps {
  data: any[];
  columns: any[];
}

const StyledTable: FC<StyledTableProps> = ({ data, columns }) => {
  const { t } = useTranslation(["shared", "common"]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0, //initial page index
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  return (
    <div className="flex flex-1 overflow-auto">
      <div className="h-fit min-w-full overflow-auto rounded-lg border">
        <table
          className="min-w-full border-collapse divide-y divide-gray-200 overflow-hidden rounded-xl border"
          cellSpacing="0"
          cellPadding="0"
        >
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={`${header.column.getCanSort() ? "cursor-pointer" : ""} px-6 py-4 whitespace-nowrap ${header.column.columnDef.meta} text-sm font-medium text-gray-600`}
                  >
                    <div
                      className={`${header.column.columnDef.meta} flex flex-row items-center gap-2`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <div className="flex flex-col items-center justify-center gap-0">
                          <ChevronSvg
                            className={`-rotate-90`}
                            color={header.column.getIsSorted() === "asc" ? "#000" : "#9ca3af"}
                            width={10}
                            height={10}
                          />
                          <ChevronSvg
                            className="text-gr rotate-90"
                            color={header.column.getIsSorted() === "desc" ? "#000" : "#9ca3af"}
                            width={10}
                            height={10}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border border-solid border-gray-200 bg-white">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="w-[1%] px-6 py-4 whitespace-nowrap first:w-full">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-white px-6 py-4">
              <td
                key={1}
                className="px-6 py-4 text-sm font-medium text-gray-700"
                colSpan={columns.length}
              >
                <div className="flex flex-row gap-10">
                  <div className="flex gap-1 sm:gap-3">
                    <button
                      className={`rounded-lg border border-solid border-gray-200 text-xs sm:text-sm ${!table.getCanPreviousPage() ? "bg-gray-200" : "bg-white"} px-3 py-2 text-gray-700`}
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {t("common:actions.previous")}
                    </button>
                    <button
                      className={`rounded-lg border border-solid border-gray-200 text-xs sm:text-sm ${!table.getCanNextPage() ? "bg-gray-200" : "bg-white"} px-3 py-2 text-gray-700`}
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      {t("common:actions.next")}
                    </button>
                  </div>
                  <div className="flex w-full items-center">
                    <span className="hidden align-middle sm:block">
                      {t("table.pageOf", {
                        page: table.getState().pagination.pageIndex + 1,
                        pageCount: table.getPageCount().toLocaleString(),
                      })}
                    </span>
                    <span className="block align-middle sm:hidden">
                      {table.getState().pagination.pageIndex + 1}/
                      {table.getPageCount().toLocaleString()}
                    </span>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default StyledTable;
